import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ExpenseReport } from "../models/expense_report";

export const downloadPDF = (
  reports: ExpenseReport[],
  exportName: string,
  groupBy: string = "all",
  t: any,
) => {
  const doc = new jsPDF();

  const translateCat = t?.categoryTypes || {};
  const tStatus = t?.expenseReports || {};

  const getStatusLabel = (status: string) => {
    if (!status) return "";
    const map: Record<string, string> = {
      DRAFT: tStatus.statusDraft,
      PENDING: tStatus.statusPending,
      APPROVED: tStatus.statusApproved,
      REJECTED: tStatus.statusRejected,
      REIMBURSED: tStatus.statusReimbursed,
    };
    return map[status.toUpperCase()] || status;
  };

  doc.addImage("/logo_notacito.png", "PNG", 14, 10, 16, 16);

  doc.setFontSize(22);
  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "bold");
  doc.text("Notacito", 34, 22);

  doc.setFontSize(16);
  doc.setFont("helvetica", "normal");
  // Titre principal traduit
  doc.text(
    `${t?.expenseReports?.title || "Notes de frais"} - ${exportName}`,
    14,
    40,
  );

  doc.setFontSize(10);
  doc.setTextColor(100, 116, 139);
  const locale = t?.common?.locale || "fr-FR";
  doc.text(
    `${t?.common?.documentGeneratedOn || "Document généré le :"} ${new Date().toLocaleDateString(locale)}`,
    14,
    47,
  );

  const tableColumn = [
    t?.expenseReports?.tableDate || "Date",
    t?.expenseReportDetails?.fields?.user || "Collaborateur",
    t?.expenseReports?.tableCategory || "Catégorie",
    t?.expenseReports?.tableSupplier || "Fournisseur",
    t?.expenseReportDetails?.fields?.comment || "Description",
    t?.expenseReportDetails?.itemFields?.amountHt || "HT",
    t?.expenseReports?.tableVat || "TVA",
    t?.expenseReportDetails?.itemFields?.totalAmountTtc || "TTC",
    t?.expenseReports?.tableStatus || "Statut",
  ];

  let startY = 55;

  if (groupBy === "category") {
    const groupedReports: Record<string, ExpenseReport[]> = {};

    reports.forEach((r) => {
      const rawCat = r.category?.item_type || "other";
      const displayCat = translateCat[rawCat.toLowerCase()] || rawCat;

      if (!groupedReports[displayCat]) groupedReports[displayCat] = [];
      groupedReports[displayCat].push(r);
    });

    Object.entries(groupedReports).forEach(([displayCat, items]) => {
      if (startY > 250) {
        doc.addPage();
        startY = 20;
      }

      doc.setFontSize(12);
      doc.setTextColor(15, 23, 42);
      doc.setFont("helvetica", "bold");
      doc.text(
        `${t?.expenseReports?.tableCategory || "Famille"} : ${displayCat}`,
        14,
        startY,
      );
      startY += 5;

      let catHT = 0,
        catVAT = 0,
        catTTC = 0;
      const tableRows = items.map((r) => {
        const date = r.expense_date
          ? new Date(r.expense_date).toLocaleDateString(locale)
          : "";
        const actualEmployee =
          `${r.user?.first_name || ""} ${r.user?.last_name || ""}`.trim() ||
          "-";
        const supplier = r.supplier_name || "";
        const desc = r.comment || "";
        const shortDesc =
          desc.length > 20 ? desc.substring(0, 17) + "..." : desc;

        const ttc = Number(r.amount) || 0;
        const vat = Number(r.vat_amount) || 0;
        const ht = ttc - vat;

        catTTC += ttc;
        catVAT += vat;
        catHT += ht;

        return [
          date,
          actualEmployee,
          displayCat,
          supplier,
          shortDesc,
          `${ht.toFixed(2)} €`,
          `${vat.toFixed(2)} €`,
          `${ttc.toFixed(2)} €`,
          getStatusLabel(r.status || ""),
        ];
      });

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: startY,
        theme: "striped",
        styles: { fontSize: 7 },
        headStyles: { fillColor: [40, 91, 177] },
        foot: [
          [
            "",
            "",
            "",
            "",
            `${t?.expenseReports?.totals || "TOTAUX"} ${displayCat.toUpperCase()}`,
            `${catHT.toFixed(2)} €`,
            `${catVAT.toFixed(2)} €`,
            `${catTTC.toFixed(2)} €`,
            "",
          ],
        ],
        footStyles: {
          fillColor: [241, 245, 249],
          textColor: [15, 23, 42],
          fontStyle: "bold",
        },
      });

      startY = (doc as any).lastAutoTable.finalY + 15;
    });
  } else {
    let totalTTC = 0,
      totalHT = 0,
      totalVAT = 0;

    const sortedReports = [...reports].sort(
      (a, b) =>
        new Date(a.expense_date || 0).getTime() -
        new Date(b.expense_date || 0).getTime(),
    );

    const tableRows = sortedReports.map((r) => {
      const date = r.expense_date
        ? new Date(r.expense_date).toLocaleDateString(locale)
        : "";
      const actualEmployee =
        `${r.user?.first_name || ""} ${r.user?.last_name || ""}`.trim() || "-";

      const rawCat = r.category?.item_type || "other";
      const displayCat = translateCat[rawCat.toLowerCase()] || rawCat;

      const supplier = r.supplier_name || "";
      const desc = r.comment || "";
      const shortDesc = desc.length > 20 ? desc.substring(0, 17) + "..." : desc;

      const ttc = Number(r.amount) || 0;
      const vat = Number(r.vat_amount) || 0;
      const ht = ttc - vat;

      totalTTC += ttc;
      totalVAT += vat;
      totalHT += ht;

      return [
        date,
        actualEmployee,
        displayCat,
        supplier,
        shortDesc,
        `${ht.toFixed(2)} €`,
        `${vat.toFixed(2)} €`,
        `${ttc.toFixed(2)} €`,
        getStatusLabel(r.status || ""),
      ];
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: startY,
      theme: "striped",
      styles: { fontSize: 7 },
      headStyles: { fillColor: [40, 91, 177] },
      foot: [
        [
          "",
          "",
          "",
          "",
          t?.common?.totals || "TOTAUX",
          `${totalHT.toFixed(2)} €`,
          `${totalVAT.toFixed(2)} €`,
          `${totalTTC.toFixed(2)} €`,
          "",
        ],
      ],
      footStyles: {
        fillColor: [241, 245, 249],
        textColor: [15, 23, 42],
        fontStyle: "bold",
      },
    });
  }

  const pageHeight =
    doc.internal.pageSize.height || doc.internal.pageSize.getHeight();
  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  doc.setFont("helvetica", "normal");

  doc.text("RK Development  - FRANCE", 105, pageHeight - 15, {
    align: "center",
  });
  doc.text(
    "SIREN : 890322043 - Dispensé d'immatriculation au RCS et au RM",
    105,
    pageHeight - 11,
    { align: "center" },
  );
  doc.text("TVA non applicable, art. 293 B du CGI", 105, pageHeight - 7, {
    align: "center",
  });

  const filename = `export_${exportName.replace(/\s+/g, "_")}_${new Date().getTime()}.pdf`;
  doc.save(filename);
};
